import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { BuildsInProgressPanel } from '@/shared/components/BuildFlowTimeline'
import { EmptyState } from '@/shared/components/EmptyState'
import { PageIntro } from '@/shared/components/PageIntro'
import { EmptyGuide, useGuideNavigate } from '@/features/integrations/components/EmptyGuide'
import { UsingProvidersGuide } from '@/features/integrations/guides/UsingProvidersGuide'
import { GUIDED_ONBOARDING_TIERS } from '@/lib/tiers'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { Badge } from '@/shared/components/Badge'
import { RepoProviderIcon, RegistryIcon } from '@/shared/components/RepoProviderIcon'
import { Tooltip } from '@/shared/components/Tooltip'
import { SeverityDonut } from '@/shared/components/SeverityDonut'
import { IconButton } from '@/shared/components/IconButton'
import { ProviderCodeModal } from '@/features/providers/components/ProviderCodeModal'
import { ManageProviderModal } from '@/features/providers/components/ManageProviderModal'
import { ProviderPoliciesModal } from '@/features/providers/components/ProviderPoliciesModal'
import { ServeModeBadge } from '@/shared/components/ServeOverrideControl'
import { CodeIcon, ShieldIcon, PlusIcon } from '@/shared/components/Icons'
import type { OrgItem, ProviderArtefactRow } from '@/lib/apiTypes'
import { useAccessToken, useProviderArtefacts, refreshProviderStats } from '@/lib/hooks'
import { ArtefactDetailModal } from '@/features/providers/ArtefactDetailModal'
import { ConstraintCell } from '@/features/providers/components/ConstraintCell'
import { DiscoveredMark } from '@/features/providers/components/DiscoveredMark'
import { MetricsCell } from '@/features/providers/components/MetricsCell'
import { registryLabel, repoHost } from '@/features/providers/lib/versions'
import { ORIGIN_FILTERS } from '@/features/providers/originFilters'
import { type ArtefactTab } from '@/features/providers/tabs/artefactTabs'
import { type OrgLimits, type OriginFilter } from '@/features/providers/types'

// ── Unified artefact list ────────────────────────────────────────────────────
export function ProvidersList({ org, orgId, selUpstream, selNamespace, selType, limits, canAdmin }: {
  org: OrgItem
  orgId: string
  /** Registry segment of the deep link (/providers/:upstream/:namespace/:type). */
  selUpstream?: string
  selNamespace?: string
  selType?: string
  limits: OrgLimits['limits']
  canAdmin: boolean
}) {
  const navigate = useNavigate()
  const goToGuide = useGuideNavigate()
  const [searchParams] = useSearchParams()
  const { token } = useAccessToken()
  const artefacts = useProviderArtefacts(orgId)
  // Provenance + text filters are local UI state; the artefact drill-down IS in the URL
  // (/providers/:upstream/:namespace/:type) so detail links stay shareable. `?q=` seeds the text
  // filter for callers that know a provider's namespace/type but not its registry (state graph).
  const [origin, setOrigin] = useState<OriginFilter>('all')
  // "Only providers with critical or high findings" - the triage cut for a long list.
  const [severeOnly, setSevereOnly] = useState(false)
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  // `?v=` opens the artefact on its Versions tab with that version already expanded. Same idea as
  // `?q=` above - a seed for callers that know which version they care about (a workspace's
  // "Providers in use" row links straight to the version its state locks). Deliberately a query
  // param, not a path segment: the expanded version is transient UI state, and the artefact
  // itself stays the thing the path identifies.
  const initialVersion = searchParams.get('v')
  const [showManage, setShowManage] = useState(false)
  // Page-level org-baseline provider policies modal (the per-provider one is per-provider).
  const [showOrgPolicies, setShowOrgPolicies] = useState(false)
  // Consume config stays a one-click table action; Policies now opens the detail modal
  // straight to its Policies tab instead of a standalone modal (initialTab below).
  const [consumeRow, setConsumeRow] = useState<ProviderArtefactRow | null>(null)
  const [initialTab, setInitialTab] = useState<ArtefactTab>('insight')

  const rows = artefacts.data?.providers ?? []
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows
      .filter((r) => origin === 'all' || (origin === 'discovered') === r.discovered)
      .filter((r) => !severeOnly || r.critical_count > 0 || r.high_count > 0)
      .filter((r) => !q || `${r.namespace}/${r.type}`.toLowerCase().includes(q) || r.upstream.includes(q))
      .slice()
      // Same provider from both registries sorts adjacent (registry as the tiebreak) so the two
      // rows read as a pair.
      .sort((a, b) => `${a.namespace}/${a.type}`.localeCompare(`${b.namespace}/${b.type}`) || a.upstream.localeCompare(b.upstream))
  }, [rows, origin, severeOnly, search])
  const severeCount = useMemo(() => rows.filter((r) => r.critical_count > 0 || r.high_count > 0).length, [rows])

  /** After an add/edit save: refetch immediately (the row appears with dashed metrics),
   *  then await the targeted stats refresh and refetch again so stars/releases/release-dates
   *  land seconds later instead of on the next cron sweep. Best-effort - a failure here is
   *  invisible (the 15-min sweep remains the fallback). Edits run it too; for an unchanged repo
   *  the call is TTL-gated server-side to a single cache read. The registry is part of the
   *  identity, so the refresh targets exactly the (upstream, namespace, type) row saved. */
  function handleManaged(upstream: string, ns: string, type: string) {
    artefacts.refetch()
    refreshProviderStats(orgId, upstream, ns, type)
      .then(() => artefacts.refetch())
      .catch(() => { /* cron fallback */ })
  }

  // Detail is driven by the URL (/providers/:upstream/:namespace/:type) so it is deep-linkable.
  // The registry is part of the identity: hashicorp/local may be listed once per registry.
  const selected = (selUpstream && selNamespace && selType)
    ? rows.find((r) => r.upstream === selUpstream && r.namespace === selNamespace && r.type === selType)
      ?? { upstream: selUpstream, namespace: selNamespace, type: selType }
    : null

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {selected && (
        <ArtefactDetailModal
          orgId={orgId}
          upstream={selected.upstream}
          namespace={selected.namespace}
          type={selected.type}
          canAdmin={canAdmin}
          limits={limits}
          initialTab={initialTab}
          initialVersion={initialVersion}
          onClose={() => { navigate(`/org/${orgId}/providers`); setInitialTab('insight') }}
          onChanged={artefacts.refetch}
          onManaged={handleManaged}
        />
      )}

      {showManage && (
        <ManageProviderModal
          orgId={orgId}
          onClose={() => setShowManage(false)}
          onCreated={handleManaged}
        />
      )}

      {consumeRow && consumeRow.latest_version && (
        <ProviderCodeModal
          orgId={orgId}
          upstream={consumeRow.upstream}
          namespace={consumeRow.namespace}
          type={consumeRow.type}
          version={consumeRow.latest_version}
          onClose={() => setConsumeRow(null)}
        />
      )}

      {showOrgPolicies && (
        <ProviderPoliciesModal
          orgId={orgId}
          canAdmin={canAdmin}
          onClose={() => setShowOrgPolicies(false)}
        />
      )}

      <PageIntro pageKey="providers" title="What is this page?">
        <p style={{ margin: '0 0 10px' }}>
          Providers your modules and workspaces reference are <strong>discovered automatically</strong> from
          module <code className="mono">required_providers</code> blocks and workspace lock files - you
          rarely need to add one by hand. Every provider listed here is managed the same way: scanned for
          CVEs, built from source with any fixable CVEs patched, pinned, signed, and served to your
          workspaces through this org's registry replacing the upstream version.
        </p>
        <p style={{ margin: 0 }}>
          Open a provider to see its full version history, vulnerability findings, download trends, and
          which workspaces or modules use it; from there you can also consume its config, set org or
          per-provider policies, control whether it's served (Off / Serve / Block), edit its version
          constraint, or remove it.
        </p>
      </PageIntro>

      {/* In-flight pipeline state - a separate surface from the verified versions list below
          (isVersionVisible is untouched: nothing unverified ever appears as a version). */}
      <BuildsInProgressPanel orgId={orgId} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div role="group" aria-label="Filter by provenance" style={{ display: 'flex', gap: 4 }}>
          {ORIGIN_FILTERS.map((f) => (
            <Tooltip key={f.key} text={f.tip} placement="bottom">
              <button
                className={`btn btn-sm ${origin === f.key ? 'btn-primary' : 'btn-ghost'}`}
                aria-pressed={origin === f.key}
                onClick={() => setOrigin(f.key)}
              >
                {f.label}
              </button>
            </Tooltip>
          ))}
          <span aria-hidden style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 4px', alignSelf: 'center' }} />
          <Tooltip text="Only providers with at least one critical or high vulnerability finding in their latest version." placement="bottom">
            <button
              className={`btn btn-sm ${severeOnly ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={severeOnly}
              onClick={() => setSevereOnly((s) => !s)}
            >
              Critical or high{severeCount > 0 ? ` (${severeCount})` : ''}
            </button>
          </Tooltip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            className="input"
            style={{ maxWidth: 200 }}
            placeholder="Filter…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Visible to every member: PolicyOverrides renders read-only without canAdmin. */}
          <button className="btn btn-ghost btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowOrgPolicies(true)}>
            <ShieldIcon /> Org policies
          </button>
          {canAdmin && (
            <button className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowManage(true)}>
              <PlusIcon size={13} /> Add provider
            </button>
          )}
        </div>
      </div>

      {artefacts.loading ? (
        <SkeletonTable headers={['Provider', 'Auto-discovered', 'Upstream latest', 'TerraMantle Version', 'Constraint', 'Findings', 'Metrics', 'Actions']} rows={5} />
      ) : artefacts.error ? (
        <div className="feedback-error">{String(artefacts.error)}</div>
      ) : rows.length === 0 ? (
        GUIDED_ONBOARDING_TIERS.has(org.tier) ? (
          <EmptyGuide
            icon="⬡"
            title="No providers yet"
            blurb="Providers are discovered from the modules and workspaces that reference them - from registry.terraform.io or registry.opentofu.org - then scanned, built from source and served alongside upstream, with CVE fixes applied where any were fixable. To start pulling providers through this org's registry now, follow the guide below."
            guideId="using-providers"
          >
            <UsingProvidersGuide org={org} token={token} onSwitchGuide={goToGuide} />
          </EmptyGuide>
        ) : (
          <EmptyState icon="⬡" title="No providers yet" subtitle="Providers your modules and workspaces reference are discovered, scanned, built from source and served here automatically, alongside upstream. Each provider is tracked per registry (registry.terraform.io and registry.opentofu.org are separate lineages), so the same provider can appear once for each. Add one by hand only when nothing references it yet." />
        )
      ) : filtered.length === 0 ? (
        <EmptyState icon="⬡" title="No providers match your filter" />
      ) : (
        <div className="table-wrap fu">
          <table>
            <thead>
              <tr>
                <th>
                  <Tooltip text="The provider's namespace/type and the upstream registry it is served from." placement="top">
                    <span tabIndex={0} className="cursor-help">Provider</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Tick: surfaced automatically from a lock file or module required_providers. Cross: added by hand. Both are managed, scanned and served per the serve mode." placement="top">
                    <span tabIndex={0} className="cursor-help">Auto-discovered</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Newest version published upstream - not necessarily the newest one served here." placement="top">
                    <span tabIndex={0} className="cursor-help">Upstream latest</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="TerraMantle's patched version of this provider: the upstream release forked and CVE-patched where fixes existed, then rebuilt from source - pinned, gate-passed, signed and scanned. The trailing segment is TerraMantle's patch revision of that upstream version." placement="top">
                    <span tabIndex={0} className="cursor-help">TerraMantle Version</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Version constraint on what this org serves for the provider - only matching versions are listed and downloadable. Semver operators, comma-separated for a combined range (for example >= 4.0, < 6.0). Empty means all versions." placement="top">
                    <span tabIndex={0} className="cursor-help">Constraint</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Critical (C) and high (H) vulnerability counts from the latest version's completed scan - never an older version's. Open the provider for the per-version breakdown." placement="top">
                    <span tabIndex={0} className="cursor-help">Findings</span>
                  </Tooltip>
                </th>
                <th>
                  <Tooltip text="Repo health and usage: stars, then commits, releases and TerraMantle downloads over the last 90 days. A dash means no cached repo stats yet." placement="top">
                    <span tabIndex={0} className="cursor-help">Metrics</span>
                  </Tooltip>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="row-link"
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${r.namespace}/${r.type} provider detail`}
                  onClick={() => navigate(`/org/${orgId}/providers/${encodeURIComponent(r.upstream)}/${encodeURIComponent(r.namespace)}/${encodeURIComponent(r.type)}`)}
                  onKeyDown={(e) => {
                    // Enter/Space activate the row like a link, but only when focus is on the row
                    // itself - never swallow keys aimed at an inner control (badge, action button).
                    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                      e.preventDefault()
                      navigate(`/org/${orgId}/providers/${encodeURIComponent(r.upstream)}/${encodeURIComponent(r.namespace)}/${encodeURIComponent(r.type)}`)
                    }
                  }}
                >
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                      {r.source_url && (
                        repoHost(r.source_url) ? (
                          <Tooltip text={`Open the source repository on ${repoHost(r.source_url) === 'github' ? 'GitHub' : 'GitLab'}`} placement="top">
                            <a
                              href={r.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`Open ${r.namespace}/${r.type} source repository`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ display: 'inline-flex', alignItems: 'center', color: 'inherit' }}
                            >
                              <RepoProviderIcon provider={repoHost(r.source_url)} />
                            </a>
                          </Tooltip>
                        ) : (
                          <RepoProviderIcon provider={null} />
                        )
                      )}
                      <code className="mono" style={{ fontSize: '0.8rem' }}>{r.namespace}/{r.type}</code>
                      <ServeModeBadge mode={r.serve_mode} />
                      {/* Lives on the name now that the usage columns are gone - a workspace
                          locking a version behind latest_matching is worth seeing in the list. */}
                      {r.outdated && (
                        <Tooltip text="At least one workspace locks a version behind the newest one this org serves for the provider." placement="top">
                          <span tabIndex={0} style={{ display: 'inline-flex', cursor: 'help' }}>
                            <Badge variant="amber">Outdated</Badge>
                          </span>
                        </Tooltip>
                      )}
                    </span>
                  </td>
                  <td><DiscoveredMark row={r} compact /></td>
                  <td>
                    {r.upstream_latest_version ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Tooltip text={`Latest release on ${registryLabel(r.upstream)}`} placement="top">
                          <span tabIndex={0} style={{ display: 'inline-flex', cursor: 'help', color: 'var(--text2)' }} aria-label={registryLabel(r.upstream)}>
                            <RegistryIcon upstream={r.upstream} />
                          </span>
                        </Tooltip>
                        <code className="mono" style={{ fontSize: '0.78rem' }}>{r.upstream_latest_version}</code>
                      </span>
                    ) : r.latest_version ? (
                      // Lookup hasn't run yet - fall back to the newest CACHED version, dimmed so
                      // it doesn't read as the registry's latest.
                      <Tooltip text="Upstream latest not resolved yet - showing the newest cached version." placement="top">
                        <span tabIndex={0} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text3)', cursor: 'help' }}>
                          <RegistryIcon upstream={r.upstream} />
                          <code className="mono" style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>{r.latest_version}</code>
                        </span>
                      </Tooltip>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                  <td>
                    {r.latest_build_version ? (
                      <code className="mono" style={{ fontSize: '0.78rem' }}>{r.latest_build_version}</code>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                  <td><ConstraintCell value={r.version_constraint} /></td>
                  <td>
                    <SeverityDonut
                      critical={r.critical_count}
                      high={r.high_count}
                      total={r.vuln_count}
                      footnote={`Latest version only (${r.latest_version ?? 'unresolved'}) - an older version may carry different findings.`}
                    />
                    {r.vuln_count === 0 && r.critical_count === 0 && r.high_count === 0 && (
                      <Tooltip
                        text={
                          r.latest_version
                            ? `No findings on the latest version (${r.latest_version}) - an older version may carry findings of its own.`
                            : 'No cached version to scan yet.'
                        }
                        placement="top"
                      >
                        <span tabIndex={0} style={{ color: 'var(--text3)', cursor: 'help' }}>—</span>
                      </Tooltip>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}><MetricsCell row={r} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                      <IconButton
                        label={`Consume config for ${r.namespace}/${r.type}`}
                        tip={`Consume config pinned to v${r.latest_version ?? '?'} - required_providers block, your registry endpoint and CLI steps.`}
                        disabledReason={r.latest_version ? null : 'No cached versions to consume yet.'}
                        onClick={() => setConsumeRow(r)}
                      >
                        <CodeIcon />
                      </IconButton>
                      <IconButton
                        label={`Policies for ${r.namespace}/${r.type}`}
                        tip="Policies: override the inherited org baseline or author custom policies for this provider."
                        onClick={() => {
                          setInitialTab('policies')
                          navigate(`/org/${orgId}/providers/${encodeURIComponent(r.upstream)}/${encodeURIComponent(r.namespace)}/${encodeURIComponent(r.type)}`)
                        }}
                      >
                        <ShieldIcon />
                      </IconButton>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
